import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	EventEmitter,
	inject,
	Injectable,
	OnDestroy,
	OnInit,
	Signal,
	ViewChild
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RealtimeWrapperComponent } from '@app/customizer-shared/components/realtime-wrapper/realtime-wrapper';
import { SummarySidebarComponent } from '@app/customizer-shared/components/summary-sidebar/summary-sidebar.component';
import { AvailabilityComponent } from '@app/shared/components/availability/availability-text.component';
import { CopyrightComponent } from '@app/shared/components/copyright/copyright.component';
import { UserPreferencesStore } from '@app/shared/stores/user-preferences.store';
import { BrandEnum } from '@infrastructure/brand.enum';
import { CameraEnum } from '@infrastructure/camera.enum';
import { CustomizerDependencyCalculatorFactory } from '@infrastructure/dependency/customizer-dependency-calculator';
import { DependencyCalculator } from '@infrastructure/dependency/dependency-calculator';
import { FirstPair } from '@infrastructure/firstPair.enum';
import { Gender } from '@infrastructure/gender.enum';
import { LogLevelEnum } from '@infrastructure/logLevel.enum';
import { OrderCanceledError } from '@infrastructure/order-canceled-error';
import ValidationResult from '@infrastructure/validation-result';
import { OrderChooserModalComponent } from '@modals/order-chooser-modal/order-chooser-modal.component';
import { StartKitsModalComponent, StartKitsModalData } from '@modals/start-kits-modal/start-kits-modal.component';
import { AddOrderItemExceptionModel } from '@models/add-order-item-exception-model';
import { StartkitMapping } from '@models/api-models/startkit-mapping.interface';
import { CustomerModel } from '@models/customer';
import { CustomizerParams } from '@models/customizer-params';
import { CustomizerRouteParams } from '@models/customizer-route-params';
import { CustomizerSummaryModel } from '@models/customizer-summary-model';
import {
	DiscountModel,
	DiscountModelFactory,
} from '@models/discounts/discount-model';
import { VariantsToDiscountModel } from '@models/iorderitem-discountvalidation';
import { MenuModel } from '@models/menu';
import { MenuComponentModel } from '@models/menu-component';
import { MiscModel } from '@models/misc-model';
import { PriceResponse } from '@models/price-model';
import { TempleInclinationEnum } from '@models/variant-temple-inclination';
import { TranslatePipe } from '@ngx-translate/core';
import { BrandModeService } from '@services/brandmode.service';
import { BreadcrumbService } from '@services/breadcrumb.service';
import { ConfigurationService } from '@services/configuration.service';
import { CustomizerService } from '@services/customizer.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { LanguageService } from '@services/language.service';
import { ManualConfigService } from '@services/manual-config.service';
import { ModalService } from '@services/modal.service';
import { OrderItemService } from '@services/order-item.service';
import { OrderService } from '@services/order.service';
import { OrdersDataService } from '@services/orders-data.service';
import {
	SubMenuStateEnum,
	SubMenuStateTogglerService,
} from '@services/sub-menu-state-toggler.service';
import { UserService } from '@services/user.service';
import { BagExecutionService } from '@services/v2/bag-execution.service';
import { ModelsService } from '@services/v2/models.service';
import { StartkitMappingService } from '@services/v2/startkit-mapping.service';
import { AppState } from '@shared/app-state';
import { DiscountConstants } from '@shared/discount-constants';
import { GrowTransition } from '@shared/transitions/grow.transition';
import { UserTypeConstant } from '@shared/user-type-constants';
import { BehaviorSubject, concatMap, Observable, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CustomizerSummaryModelFactory } from '@factories/CustomizerSummaryModelFactory';
import { ApiResponseModalComponent } from '@modals/api-response/api-response.component';
import { DisplayEnduserModalComponent } from '@modals/display-enduser/display-enduser.component';
import { GenericInfoModalComponent } from '@modals/generic/generic-info.modal';
import { ComponentVariantFromApiModel } from '@models/api-models/component-variant-fromapi';
import { ModelModel } from '@models/api-models/model';
import { ChosenVariantsModel } from '@models/chosen-variants';
import { ComponentVariantModel } from '@models/component-variant';
import { ComponentVariantChangeModel } from '@models/component-variant-change';
import { ConfigurationModel } from '@models/configuration';
import { ConfigurationPlusModel } from '@models/configuration-plus-model';
import { CustomizerMenuPartModel } from '@models/customizer-menu-part';
import { ErrorModel } from '@models/error';
import { ModelGroupModel } from '@models/model-group';
import { ModelSizeModel } from '@models/model-size';
import { OrderItemModel } from '@models/order-item';
import { OrderLine } from '@models/order-line';
import { UserDataModel } from '@models/user-data-model';
import { VariantColorModel } from '@models/variant-color';
import { DataApiService } from '@services/data-api.service';
import { EnduserDisplayService } from '@services/enduserdisplay.service';
import { EngravingService } from '@services/engraving.service';
import { EventsService } from '@services/events.service';
import {
	ConfigurationMode,
	ImageService,
	ImageSize
} from '@services/image.service';
import { LogEvent, LoggerService } from '@services/logger.service';
import { MenuService } from '@services/menu.service';
import { NavigateService } from '@services/navigate.service';
import { OrderOnBehalfService } from '@services/order-on-behalf.service';
import { SorterService } from '@services/sorter.service';
import { TitleService } from '@services/title.service';
import { BasePage } from '@infrastructure/base-page';
import { AngleConstants } from '@shared/angle-constants';
import { ComponentCodeConstants } from '@shared/component-constants';
import { CustomizerInspirationStateConstants } from '@shared/customizer-inspiration-state-constants';
import { CustomizerStateConstants } from '@shared/customizer-state-constants';
import { ErrorResponseConstants } from '@shared/error-response-constants';
import { MenuConstants } from '@shared/menu-constants';
import { PermissionConstants } from '@shared/permission-constants';
import { ProductlineConstants } from '@shared/productline-constants';
import { FadeInOutTransition } from '@shared/transitions/fade-in-out.transition';
import { CustomizerSummaryComponent } from '../customizer-shared/components/customizer-summary/customizer-summary';
import { BreadcrumbComponent } from '../shared/components/breadcrumb/breadcrumb';
import { SunDetailsStore } from '../shared/stores/sun-details.store';
import {
	CustomizerChangesComponent,
	CustomizerComponentLoaderComponent,
	CustomizerMenuComponent,
} from './components';
import { AddToBagButtonComponent } from './components/add-to-bag-button/add-to-bag-button.component';
import { AddToInspirationComponent } from './components/add-to-inspiration/add-to-inspiration';
import { FavoritesAreaComponent } from './components/favorites-area/favorites-area.component';
import { GetPriceComponent } from './components/get-price/get-price.component';
import { SetDefaultButtonComponent } from './components/set-default-button/set-default-button';
import { SunLenseInfoComponent } from './components/sun-lense-info/sun-lense-info.component';

@Component({
	templateUrl: 'customizer.html',
	styleUrls: ['./customizer.scss'],
	animations: [
		FadeInOutTransition(),
		FadeInOutTransition(0.3, 500, 500, 'fadeInOutLowOpacity'),
		GrowTransition(),
	],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		AvailabilityComponent,
		RealtimeWrapperComponent,
		CommonModule,
		TranslatePipe,
		FavoritesAreaComponent,
		GetPriceComponent,
		AddToInspirationComponent,
		SetDefaultButtonComponent,
		CopyrightComponent,
		CustomizerChangesComponent,
		CustomizerComponentLoaderComponent,
		SummarySidebarComponent,
		CustomizerMenuComponent,
		AddToBagButtonComponent,
		BreadcrumbComponent,
		SunLenseInfoComponent,
		CustomizerSummaryComponent,
	],
})
export class CustomizerPageComponent extends BasePage implements OnInit, OnDestroy {

	userPreferencesStore = inject(UserPreferencesStore);

	cameraEnum = CameraEnum;

	public isUserInAdminMode: boolean;
	public user: UserDataModel | null;
	public userCanEditinspiration: boolean;

	// Current chosen elements
	public currentEngraving: string;
	public currentReference: string;
	public currentComment: string;
	public chosenTempleInclination?: TempleInclinationEnum | null = null;
	public chosenCase?: MiscModel | null;
	public model: ModelModel | undefined;
	public modelGroup: ModelGroupModel | undefined;
	public chosenModelSize: ModelSizeModel | undefined;
	public chosenVariants: ChosenVariantsModel;
	public baseConfiguration: ConfigurationModel | null;
	public hasForms: boolean = false;
	public hasFixtures: boolean = false;

	// Client Personal Information
	public age: string | null = null;
	public firstPair: FirstPair | null = null;
	public gender: Gender | null = null;

	// To update values, used when savechanges are called
	private referenceToUpdate: string | null;
	private commentToUpdate: string | null;
	private engravingToUpdate: string | null;
	public configurationToUpdate: ConfigurationModel | null;
	public variantsToUpdate: Array<ComponentVariantChangeModel> | undefined;
	public modelToUpdate: ModelModel | undefined;
	public modelsizeToUpdate: ModelSizeModel | undefined;
	public discountToReset: boolean;
	public templeInclinationToUpdate: TempleInclinationEnum | undefined;
	public caseToUpdate: MiscModel | null;
	public removeCase: boolean;
	public errors: Array<ErrorModel> = [];

	// Menu
	public components: Array<CustomizerMenuPartModel>;

	public currentSubComponent: CustomizerMenuPartModel | undefined;
	public currentMainComponent: CustomizerMenuPartModel | undefined;
	public currentActualComponent: CustomizerMenuPartModel | undefined;

	public allConfigurations: Array<ConfigurationModel>;

	// Current image perspektive
	public currentImageName: AngleConstants = AngleConstants.Perspective;
	public imageOnlyVariants: Array<ComponentVariantModel> = [];
	public clipOnOnly: boolean = false;
	public clipOnIncluded?: boolean = false;
	public discount: DiscountModel | null = null;
	public discountErrors: ValidationResult = new ValidationResult();

	// Current mouse sum coordinate, used to decide if click is supposed to close menu or slide image
	private mouseSumCoordinate: number;

	// Others
	public customizerStateEnum = CustomizerStateConstants;
	public ProductlineConstants = ProductlineConstants;
	public currentCustomizationState: CustomizerStateConstants;
	public currentInspirationCustomizationstate: CustomizerInspirationStateConstants;
	public orderItem: OrderItemModel | null;
	public isDemo: boolean;
	public showAddedOverlay = false;
	public addedOverlay: string;

	// Observables
	public onNavigation: Subscription;
	public enduserDisplayClickedObserveable: Observable<ConfigurationMode | null>;
	public enduserDisplayChangedObserveable: Observable<ConfigurationMode>;
	public enduserDisplayChangedSubscription: Subscription;
	public displayEnduserModalComponent: any = DisplayEnduserModalComponent;

	public allVariants: Array<ComponentVariantModel> | null;

	private orderId: number | null = null;
	public showHighlightChangesOverlay: boolean = false;
	private orderSelectEmitter = new EventEmitter<number>();
	private selectedStartkitEmitter = new EventEmitter<StartKitsModalData>();
	private orderChangedEmitter = new EventEmitter<number>();

	private dependencyCalculator: DependencyCalculator;
	public variantString: string;
	public isInCollection: boolean;
	public favoriteUuid: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
	public clipOnImageUrlLow: string | null;
	public clipOnImageUrlHigh: string | null;
	public priceResponse: BehaviorSubject<PriceResponse | null> = new BehaviorSubject<PriceResponse | null>(null);

	@ViewChild(GetPriceComponent) priceComponent: GetPriceComponent;

	@ViewChild(RealtimeWrapperComponent) realtimeWrapper: RealtimeWrapperComponent;

	isLimited: boolean;
	parts: string[];
	isParts: boolean;

	showSummary: boolean = false;
	public summaryModels: Array<CustomizerSummaryModel>;
	errormodelTempleSizeTranslateName: string;
	variantsFromApiSignal: Signal<ComponentVariantFromApiModel[] | undefined> = computed(() => this.configurationService.variantsFromApi());
	currentVariantsSignal: Signal<ComponentVariantModel[] | undefined> = computed(() => this.configurationService.currentVariants());
	httpClient = inject(HttpClient);
	sunDetailsStore = inject(SunDetailsStore);
	startkitMappingService = inject(StartkitMappingService);

	showSunMessage: boolean = false;
	doShowSummary: boolean = false;

	constructor(
		titleService: TitleService,
		modalService: ModalService,
		private events: EventsService,
		navigateService: NavigateService,
		private dataApiService: DataApiService,
		private orderDataService: OrdersDataService,
		private translateService: LanguageService,
		private sorter: SorterService,
		public menuService: MenuService,
		private enduserDisplayService: EnduserDisplayService,
		public imageService: ImageService,
		private activatedRoute: ActivatedRoute,
		private engravingService: EngravingService,
		private orderOnBehalfService: OrderOnBehalfService,
		private orderService: OrderService,
		public userService: UserService,
		private customizerService: CustomizerService,
		private googleAnalyticsService: GoogleAnalyticsService,
		public brandModeService: BrandModeService,
		private manualConfigService: ManualConfigService,
		private appState: AppState,
		private orderItemService: OrderItemService,
		private modelsService: ModelsService,
		private configurationService: ConfigurationService,
		private subMenuStateTogglerService: SubMenuStateTogglerService,
		private bagExecutionService: BagExecutionService,
		private breadcrumbService: BreadcrumbService,
		private loggerService: LoggerService
	) {
		super(titleService, modalService, navigateService);

		// subscribe to menu state and hide summary if a menu item is opened
		this.subscriptions.push(this.subMenuStateTogglerService.toggle.subscribe((s) => {
			switch (s) {
				case SubMenuStateEnum.menuOpen:
					this.showSummary = false;
					break;

				default:
					break;
			}
		}));

		this.subscriptions.push(this.sunDetailsStore.showSunDetails.subscribe((x) => {
			this.showSunMessage = x;
		}));

		// effect(() => {
		// 	const threeDeeActive = this.userPreferencesStore.threeDeeActive();
		// 	const threeDeeAccess = this.getThreeDeeAccessForProductLine();

		// 	this.use3D = threeDeeActive && threeDeeAccess;
		// });
	}

	@ViewChild('realtime') realtime: RealtimeWrapperComponent;

	override ngOnDestroy() {
		// Completing the emitter makes debounceTime emit a pending order edit now, so its save starts.
		// Unsubscribing alone would drop an edit made less than 750 ms before leaving.
		this.orderChangedEmitter.complete();
		super.ngOnDestroy();
		this.enduserDisplayChangedSubscription.unsubscribe();

		if (this.realtime) {
			console.log(this.realtime);

			this.realtime.destroy();
		}
	}

	async ngOnInit() {
		this.IsReady(false);

		this.orderService.UpdateOrderIdsFromBackend();

		this.subscriptions.push(
			this.orderSelectEmitter.subscribe(async (selectedOrderId) => {
				this.orderId = selectedOrderId;
				await this.AddToBag(selectedOrderId === null, true);
			})
		);

		this.subscriptions.push(
			this.selectedStartkitEmitter.subscribe(
				async ({ orderItem, customerNo, startKitModel, newOrderSelected }) => {
					if (startKitModel) {
						this.setAddedToBag();
						this.executeStartkitAddToBagWithHandling(orderItem, customerNo, startKitModel, newOrderSelected);
					}
				}
			)
		);

		this.dependencyCalculator = CustomizerDependencyCalculatorFactory.getDependencyCalculator(this.GetChoosableVariants);

		this.enduserDisplayClickedObserveable = this.enduserDisplayService.enduserDisplayClickedObservable();
		this.enduserDisplayChangedObserveable = this.enduserDisplayService.enduserDisplayChangedObservable();
		this.enduserDisplayChangedSubscription = this.enduserDisplayChangedObserveable.subscribe(async (x) => {
			if (this.isReady) {
				this.events.showLoader(true);

				await this.onEnduserDisplayChanged();

				this.events.showLoader(false);
			}
		});

		this.subscriptions.push(
			this.userService.adminModeEnabled.subscribe((x) => {
				this.isUserInAdminMode = x;
			})
		);

		await this.init();
	}

	private async init(closeModals = true, initFromUrl = true) {
		this.user = await this.userService.GetUser();

		await this.initSessionData(initFromUrl);

		this.titleService.setTitle(this.model?.Code);

		await this.initData(this.model);

		this.userCanEditinspiration = this.user?.UserPermissions?.some(
			(x) => x == PermissionConstants.CanEditManualConfigurations
		) ?? false;

		this.IsReady(true);

		if (closeModals) {
			this.modalService.closeAll();
		}

		if (this.priceComponent) {
			this.priceComponent.reset();
		}
	}

	private async InitSessiondataFromUrl(): Promise<
		CustomizerRouteParams | undefined
	> {
		const root: ActivatedRoute = this.activatedRoute.root;
		const modelgroupCodeFromUrl = this.breadcrumbService.getModelgroupFromBreadcrumb(root);
		const modelgroupNameUrl = this.breadcrumbService.getModelgroupName(root);

		let modelGroups = await this.modelsService.getModelGroupsFromName(modelgroupCodeFromUrl);

		if (modelgroupNameUrl && modelgroupNameUrl.length > 0) {
			modelGroups = modelGroups.flatMap(group => group.ModelGroupChildren).filter(child => child != null).filter(child => child.Description?.Fallback == modelgroupNameUrl);
		}

		const routeParams = this.customizerService.getCustomizerRouteParams();
		const routeModel = routeParams?.model;

		const modelFromUrl = this.breadcrumbService.getModelFromBreadcrumb(root);

		if ((!routeModel && modelFromUrl) || (routeModel && routeModel.Code != modelFromUrl)) {
			const model = await this.modelsService.getModelFromName(
				this.user,
				modelGroups,
				modelFromUrl
			);
			const modelGroup = modelGroups?.find(x => x.Uuid === model?.ModelGroupUuid);

			const qp = this.activatedRoute.snapshot.params;
			if (qp && qp['p']) {
				this.isParts = true;

				let cp = new CustomizerParams();
				cp.isParts = true;

				return new CustomizerRouteParams(
					modelGroup,
					model,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					cp
				);
			}
			else {
				return new CustomizerRouteParams(modelGroup, model);
			}
		}

		return undefined;
	}

	private async initSessionData(initFromUrl = true) {
		console.debug('Initializing new data from session');
		const result = await this.InitSessiondataFromUrl();
		await this.customizerService.fetchCustomizerData(result);

		this.orderItem = this.customizerService.getOrderItemData();
		this.model = this.customizerService.getModelData();

		this.allVariants = this.customizerService.getAllVariants();
		this.modelGroup = this.customizerService.getModelGroupData();
		this.baseConfiguration = this.customizerService.getConfiguration();
		this.currentCustomizationState = this.orderItem
			? CustomizerStateConstants.EDITORDER
			: CustomizerStateConstants.NEWORDER;
		this.currentInspirationCustomizationstate =
			CustomizerInspirationStateConstants.NEW;
		this.chosenModelSize = this.customizerService.getModelSize();
		this.currentReference = this.orderItem ? this.orderItem.Reference : '';
		this.currentComment = this.orderItem ? this.orderItem.Comment : '';
		this.currentEngraving = this.orderItem ? this.orderItem.Engraving : '';

		this.discount = this.orderItem?.DiscountCode
			? DiscountModelFactory.CreateDiscountModel(
				this.orderItem?.DiscountCode,
				this.orderItem.DiscountPercent
			)
			: null;

		this.hasFixtures = this.orderItem
			? this.orderItem.HasFixtures
			: this.customizerService.getCustomizerParams()?.hasFixtures ??
			false;

		this.hasForms = this.orderItem
			? this.orderItem.HasForms
			: this.customizerService.getCustomizerParams()?.hasForms ?? false;

		this.isLimited = (this.customizerService.getCustomizerParams()?.isLimited ?? false) || this.hasLimitedTempleVariantSelected();
		this.isParts = this.customizerService.getCustomizerParams()?.isParts ?? false;
		this.gender = this.orderItem?.Gender ?? null;
		this.age = this.orderItem?.Age ?? null;
		this.firstPair = this.orderItem?.FirstPair ?? null;

		if (this.model && this.model.LowTempleInclinationPossible) {
			this.chosenTempleInclination = this.orderItem
				? this.orderItem.TempleInclination
				: this.customizerService.getTempleInclination() ?? TempleInclinationEnum.standard;
		}
		else {
			this.chosenTempleInclination = TempleInclinationEnum.standard;
		}

		this.chosenCase = this.orderItem && this.orderItem.CaseItemNo && this.orderItem.CaseItemVariantCode
			? await this.modelsService.getCaseMiscModel(this.orderItem.CaseItemNo, this.orderItem.CaseItemVariantCode)
			: this.customizerService.getCase();

		if (this.brandModeService.isPrecious) {
			this.chosenCase = undefined;
		}

		this.isDemo = this.enduserDisplayService.IsDisplay();

		const variantString = this.manualConfigService.getVariantString(this.model, this.allVariants);

		this.variantString = variantString;

		this.engravingService.init(this.currentEngraving);

		this.subscriptions.push(this.orderChangedEmitter.pipe(
			debounceTime(750),
			concatMap(() => this.updateOrderOnChanged().catch((error) => {
				console.error(error);
			}))
		).subscribe());

		this.activatedRoute.queryParams.subscribe((qp) => {
			if (qp && qp['p']) {
				this.isParts = true;

				let cp = new CustomizerParams();
				cp.isParts = true;

				return this.customizerService.setCustomizerRouteParams(
					this.modelGroup,
					this.model,
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					cp
				);
			}
			else {
				return this.customizerService.setCustomizerRouteParams(this.modelGroup, this.model);
			}
		});
	}

	async initData(model: ModelModel | undefined) {
		this.currentMainComponent = undefined;
		this.currentSubComponent = undefined;

		await this.InitCurrentModelSize();
		await this.InitCurrentConfiguration(model);
		await this.InitCurrentMenus();
		await this.InitVariants();

		this.clipOnOnly = this.chosenVariants?.isSingleComponent(
			ComponentCodeConstants.Clipon
		);

		this.clipOnIncluded = this.chosenVariants?.SelectedVariants?.some(
			(variant) => variant.Component.Code == ComponentCodeConstants.Clipon
		);

		if (this.clipOnIncluded) {
			this.clipOnImageUrlLow = this.getClipOnImageUrl('Low');
			this.clipOnImageUrlHigh = this.getClipOnImageUrl('High');
		}

		this.buildSummaryModels();

		await this.CheckForDependencyChanges([]);

		if (this.orderItem) {
			console.debug('Setting order data');

			this.SetOrderlinesOnVariants(this.chosenVariants?.SelectedVariants, this.orderItem.OrderLines);

			this.enduserDisplayService.setConfigurationMode(this.orderItem.Demo, false);

			this.isDemo = this.orderItem.Demo;
		}

		try {
			var variantStrings = await this.manualConfigService.getVariantStrings();
			this.isInCollection = this.manualConfigService.isInCollectionByModelAndVariants(variantStrings, this.model, this.chosenVariants?.Variants);
			this.favoriteUuid.next(this.manualConfigService.configurationFavoriteUuidByModelAndVariants(variantStrings, this.model, this.chosenVariants?.Variants));
		}
		catch (e) {
			const error = e as any; // Type assertion
			const logEvent = new LogEvent(
				`Error setting collection and favorite data: ${error.message}`,
				LogLevelEnum.Error,
				{
					component: 'CustomizerPageComponent',
					method: 'initData (collection/favorite logic)',
					errorMessage: error.message || 'Unknown error',
					stackTrace: error.stack || 'No stack trace',
					modelCode: this.model?.Code || 'N/A',
					modelGroupCode: this.modelGroup?.Code || 'N/A'
				}
			);
			this.loggerService.sendClientLog(logEvent);
			this.isInCollection = false;
			this.favoriteUuid.next(null);
		}
	}

	private async InitCurrentModelSize() {
		if (this.model) {
			await this.dataApiService
				.getSizes(this.model.ProductLineUuid, this.modelGroup?.modelGroupDataUuid, this.model.Uuid)
				.then((modelSizeResult) => {
					if (this.chosenModelSize) {
						this.chosenModelSize = modelSizeResult?.find((x) => x.Uuid == this.chosenModelSize?.Uuid);
					}
					else {
						this.chosenModelSize = modelSizeResult?.find((x) => x.Default)
							? modelSizeResult.find((x) => x.Default)
							: modelSizeResult?.[0];
					}
				});
		}
	}

	private async InitCurrentConfiguration(model: ModelModel | undefined) {
		if (this.chosenModelSize) {
			await this.configurationService
				.getConfigurations(this.chosenModelSize.Uuid)
				.then((configResult) => {
					this.allConfigurations = configResult;

					if (!this.baseConfiguration) {
						this.baseConfiguration = configResult.find((obj) => obj.Code == model?.DefaultConfigurationCode) || configResult[0];
					}
				});
		}
	}

	private async InitCurrentMenus() {
		console.debug('Updating Menu');

		await this.menuService.getMenu(this.modelGroup).then((menus) => {
			this.components = this.menuService.ConvertMenus(menus);

			// MANUALLY ADDING PARTS MENU TO ALL MENUS
			// SHOULD PROBABLY BE MORE DYNAMIC!
			const partsEnabled = this.user?.UserPermissions?.some(
				(up) => up === PermissionConstants.PartsEnabled
			);

			if (partsEnabled) {
				let menuComponent = new MenuComponentModel();

				let menuComponentArray: MenuComponentModel[] = [];
				menuComponentArray.push(menuComponent);
				const partDesc = this.translateService.instant(
					`DOMAIN.ELEMENTS.PARTS.MENU.DESCRIPTION`
				);

				let partsMenuComponent = this.menuService.getMenuItem(
					777,
					'PartsMenuItem',
					'PARTS',
					partDesc,
					'parts',
					[], //subComponents,
					null, //parent,
					[],
					false,
					false,
					0
				);

				this.components.push(partsMenuComponent);

				if (this.isParts) {
					this.currentMainComponent = partsMenuComponent;
					this.currentActualComponent = this.currentMainComponent;
				}
			}
		});
	}

	private async InitVariants() {
		try {
			const menucomponents = this.components
				.concat(this.components.selectMany((x) => x.subComponents))
				.selectMany((x) => x.menuComponents);
			let inputVariants = this.model?.DefaultVariantModels;

			if (inputVariants) {
				if (this.currentCustomizationState !== CustomizerStateConstants.EDITORDER) {
					inputVariants = inputVariants.filter((v) => v.Brand == this.brandModeService.currentBrandValue() || v.Brand == BrandEnum.TitaniumAndPrecious);
				}

				inputVariants.forEach((inputVariant) => {
					if (inputVariant.Optional && inputVariant.Component.Code === ComponentCodeConstants.Gemstone && this.currentCustomizationState !== CustomizerStateConstants.EDITORDER) {
						inputVariant.VariantSize = null;
						inputVariant.VariantColor = undefined;
					}
				});

				let chosenVariants = new Array<ComponentVariantModel>();
				this.chosenVariants = new ChosenVariantsModel(inputVariants, this.sorter, this.GetChoosableVariants);

				const extraVariantsForSlider: Array<ComponentVariantModel> = [];

				if (this.chosenVariants.isSingleComponent(ComponentCodeConstants.Clipon)) {
					chosenVariants = inputVariants;

					this.allVariants?.forEach((orgVar) => {
						if (!inputVariants?.some((x) => x.Component.Code == orgVar.Component.Code)) {
							extraVariantsForSlider.push(orgVar);
						}
					});
				}
				else {
					if (this.allVariants) {
						this.allVariants.forEach((orgVar) => {
							// for each variant check that if inputvariants do NOT contain the variant, then push it to input - but why???
							// ie. this is where gemstone is pushed into input variants
							if (!inputVariants?.some((x) => x.Component.Code == orgVar.Component.Code)) {
								// as the variant wasn't a part of the input, I set it to notSelected...
								orgVar.NotSelected = true;
								inputVariants?.push(orgVar);
							}
						});
					}

					inputVariants.forEach((chosenVariant) => {
						if (this.modelGroup?.IsConfigurationBased || this.modelGroup?.IsBothConfigAndVariantBased) {
							chosenVariants.push(chosenVariant);
						}
						else if (menucomponents.some((x) => x.ComponentVariantUuids == chosenVariant.Uuid) || menucomponents.some((x) => x.ComponentUuids == chosenVariant.Component.Uuid)) {
							chosenVariants.push(chosenVariant);
						}
						else {
							extraVariantsForSlider.push(chosenVariant);
						}
					});
				}

				this.imageOnlyVariants = extraVariantsForSlider;
				this.chosenVariants = new ChosenVariantsModel(chosenVariants, this.sorter, this.GetChoosableVariants);
			}
		}
		catch (e) {
			const error = e as any; // Type assertion
			const logEvent = new LogEvent(
				`Error initializing variants: ${error.message}`,
				LogLevelEnum.Error,
				{
					component: 'CustomizerPageComponent',
					method: 'InitVariants',
					errorMessage: error.message || 'Unknown error',
					stackTrace: error.stack || 'No stack trace',
					modelCode: this.model?.Code || 'N/A',
					modelGroupCode: this.modelGroup?.Code || 'N/A'
				}
			);
			this.loggerService.sendClientLog(logEvent);
			this.chosenVariants = new ChosenVariantsModel([], this.sorter, this.GetChoosableVariants);
			this.imageOnlyVariants = [];
		}
	}

	private buildSummaryModels() {
		try {
			this.summaryModels = CustomizerSummaryModelFactory.buildSummaryModels(
				this.chosenVariants?.Variants
			);
		}
		catch (e) {
			const error = e as any; // Type assertion
			const logEvent = new LogEvent(
				`Error building summary models: ${error.message}`,
				LogLevelEnum.Error,
				{
					component: 'CustomizerPageComponent',
					method: 'buildSummaryModels',
					errorMessage: error.message || 'Unknown error',
					stackTrace: error.stack || 'No stack trace',
					chosenVariantsCount: this.chosenVariants?.Variants?.length?.toString() ?? '0'
				}
			);
			this.loggerService.sendClientLog(logEvent);
			this.summaryModels = [];
		}
	}

	private async onEnduserDisplayChanged() {
		this.isDemo = this.enduserDisplayService.IsDisplay();
		this.resetUnavailableForDisplayVariantsToDefault();

		this.updateCurrentComponent(undefined);
		this.updateCurrentMainComponent(undefined);

		await this.onEnduserDisplayChangedDiscountUpdate(
			this.isDemo,
			this.orderOnBehalfService.customerSnapshot()
		);

		if (this.modelGroup?.IsConfigurationBased || this.modelGroup?.IsBothConfigAndVariantBased) {
			// this is to prevent wrong variants being set later in updateDefaultVariants() caused by
			// "VariantViewed" and "VariantColorViewed" not being set in this case (configurationbased)
			await this.getAndUpdateModelSize(this.model, false);
		}
		else {
			await this.updateModel(this.model, false);
		}

		await this.updateAge(null, false);
		await this.updateFirstPair(null, false);
		await this.updateGender(null, false);
		await this.updateEngraving('', false);
		await this.saveChanges(true);

		this.engravingService.init(this.currentEngraving);
	}

	private resetUnavailableForDisplayVariantsToDefault() {
		if (!this.enduserDisplayService.IsDisplay() || !this.model?.DefaultVariantModels?.length || !this.chosenVariants?.Variants?.length) {
			return;
		}

		let resetMessages: string[] = [];

		for (let i = 0; i < this.chosenVariants.Variants.length; i++) {
			let variant = this.chosenVariants.Variants[i];

			//Checking variant.AvailableForDisplay is not helpfull as variants like Grove is always unavailable at the top level.
			let sizeUnavailable = variant.VariantSize != null && variant.VariantSize.AvailableForDisplay !== undefined && !variant.VariantSize.AvailableForDisplay;
			let colorUnavailable = variant.VariantColor != null && variant.VariantColor.AvailableForDisplay !== undefined && !variant.VariantColor.AvailableForDisplay;

			if (!sizeUnavailable && !colorUnavailable && variant.SplittingData == null) {
				continue;
			}

			let resetElements: string[] = [];

			let defaultVariant = this.model?.DefaultVariantModels.filter(
				(defaultVariant) => defaultVariant.Component.Code === variant.Component.Code
			)?.first();
			if (defaultVariant == null) {
				continue;
			}

			if (sizeUnavailable || variant.SplittingData != null) {
				this.chosenVariants.Variants[i].VariantSize = defaultVariant.VariantSize;
				this.chosenVariants.Variants[i].SplittingData = null;
				resetElements.push(this.translateService.instant('COMMON.SIZE'));
			}
			if (colorUnavailable) {
				this.chosenVariants.Variants[i].VariantColor = defaultVariant.VariantColor; //When fx temple clip size is unavailable the color is also marked unavailable in the database, causing an unwanted color reset.
				resetElements.push(resetElements.length === 0 ? this.translateService.instant('COMMON.COLOR') : ", " + this.translateService.instant('COMMON.COLOR'));
			}

			let lineText = this.translateService.instant('DISPLAYENDUSER.SWITCHRESET.AFFECTEDCOMPONENTLINE');
			let componentName = this.translateService.instant(`CUSTOMIZER.COMPONENTS.${variant.Component.Code}.NAME`);
			resetMessages.push(lineText.replace('{{componentname}}', componentName).replace('{{optionlist}}', resetElements.join('')));
		}

		if (resetMessages.length > 0) {
			this.modalService.create(GenericInfoModalComponent, {
				title: this.translateService.instant('DISPLAYENDUSER.SWITCHRESET.TITLE'),
				contentPart1: this.translateService.instant('DISPLAYENDUSER.SWITCHRESET.DESCRIPTION'),
				contentPart2: resetMessages.join('<br />')
			});
		}
	}

	private async onEnduserDisplayChangedDiscountUpdate(isDisplay: boolean, customer: CustomerModel | null) {
		const discountsList = await this.dataApiService.getDiscounts(customer, !isDisplay);

		if (this.discount && !discountsList.some((x) => x.key === this.discount?.key)) {
			await this.updateDiscount(null, false);
		}
	}

	public getClipOnImageUrl(size: ImageSize | string): string | null {
		const variants = this.chosenVariants.Variants?.filter(
			(v) => v.Component.Code === ComponentCodeConstants.Clipon
		);

		if (variants?.length === 0) {
			return null;
		}

		const cliponImageUrl = this.imageService.GetFrontImage(
			this.model,
			variants,
			size
		);

		return cliponImageUrl;
	}

	public IsReady(ready: boolean) {
		this.events.showLoader(!ready);
		this.isReady = ready;
	}

	public async setOriginalVariants(variants: Array<ComponentVariantModel>) {
		if (this.model) this.model.DefaultVariantModels = variants;

		this.allVariants = variants;

		await this.customizerService.setCustomizerRouteParams(this.modelGroup, this.model);
	}

	public async addedToInspiration() {
		this.currentInspirationCustomizationstate = CustomizerInspirationStateConstants.ADDED;

		const overlayText = this.translateService.instant(
			`CUSTOMIZER.COMPONENTS.CONFIGURATION.ADDED`
		);

		this.triggerOverlay(overlayText);
	}

	public async setAddedToBag() {
		this.currentCustomizationState = CustomizerStateConstants.ADDEDORDER;
		const customer = this.orderOnBehalfService.customerSnapshot();
		const user = await this.userService.GetUser();
		const isAddingToTestBag =
			!customer &&
			user?.canOrderOnBehalf &&
			user.Type === UserTypeConstant.Consultant;

		let addedToBagText: string;

		if (isAddingToTestBag) {
			addedToBagText = this.translateService.instant(`CUSTOMIZER.COMPONENTS.CONFIGURATION.ADDEDTO`);
			addedToBagText += "<span class='test-bag-warning'>" + this.translateService.instant(`CUSTOMIZER.COMPONENTS.CONFIGURATION.YOURTESTBAG`, { count: 1 }) + '</span>';
		}
		else {
			addedToBagText = this.translateService.instant(`CUSTOMIZER.COMPONENTS.CONFIGURATION.ADDEDFRAMETOBAG`, { count: 1 });
		}

		if (customer) {
			addedToBagText += '<br /><b>' + customer.Name + '</b>';
		}

		this.triggerOverlay(addedToBagText);
	}

	public triggerOverlay(text: string) {
		this.addedOverlay = text;
		this.showAddedOverlay = true;

		new Promise((resolve) => setTimeout(resolve, 3000)).then((x) => {
			this.showAddedOverlay = false;
		});
	}

	public async getPrice() {
		const orderItem: OrderItemModel = OrderItemModel.MapFromVariants(
			null,
			-1,
			this.isDemo,
			1,
			this.currentComment,
			this.currentReference,
			this.currentEngraving,
			this.baseConfiguration,
			this.modelGroup,
			this.model,
			this.chosenModelSize,
			this.chosenVariants.SelectedVariants,
			this.hasFixtures,
			this.hasForms,
			this.clipOnOnly,
			this.discount?.key,
			this.discount?.percentage,
			this.chosenTempleInclination,
			this.brandModeService.isPrecious,
			this.age,
			this.gender,
			this.firstPair,
			this.chosenCase
		);

		this.priceResponse.next(
			await this.orderItemService.getPriceFromConfiguration(orderItem)
		);
	}

	public async AddToBag(newOrderSelected?: boolean, fromOrderChooser: boolean = false) {
		if (await this.clientInfoIsMandatory()) {
			this.switchMenuToMiscellaneous();
			return;
		}

		if (this.appState.orderIds.length > 1 && !fromOrderChooser) {
			this.modalService.create(OrderChooserModalComponent, { orderSelect: this.orderSelectEmitter });
		}
		else {
			const customer = this.orderOnBehalfService.customerSnapshot();
			const user = await this.userService.GetUser();

			if (await this.shouldShowStartKitModal(customer, user)) {
				const orderItem = OrderItemModel.MapFromVariants(
					null,
					this.orderId,
					this.isDemo,
					1,
					this.currentComment,
					this.currentReference,
					this.currentEngraving,
					this.baseConfiguration,
					this.modelGroup,
					this.model,
					this.chosenModelSize,
					this.chosenVariants.SelectedVariants,
					this.hasFixtures,
					this.hasForms,
					this.clipOnOnly,
					this.discount?.key,
					this.discount?.percentage,
					this.chosenTempleInclination,
					this.brandModeService.isPrecious,
					this.age,
					this.gender,
					this.firstPair,
					this.chosenCase
				);

				orderItem.IsLimited = this.isLimited;

				this.modalService.create(StartKitsModalComponent, {
					resultChange: this.selectedStartkitEmitter,
					productLineCode: orderItem.ProductLineCode,
					kidTeen: this.modelGroup?.KidTeen,
					customerNo: customer?.No ?? this.userService.currentUser$.value?.CustomerNo,
					newOrderSelected: newOrderSelected,
					orderItem: orderItem
				});
				return;
			}

			this.setAddedToBag();
			this.executeAddToBagWithHandling(newOrderSelected);
		}
	}

	private async shouldShowStartKitModal(customer: CustomerModel | null, user: UserDataModel | null): Promise<boolean> {
		const testCrmTicketId = this.appState.afterSalesCRMCaseNo.value;

		if (
			customer &&
			(customer.isConsultantOrderOnBehalf || user?.IsDistributor) &&
			!testCrmTicketId
		) {
			const accessToProductLine = customer.hasProductLineAccess(
				this.modelGroup?.ProductLineCode
			);

			const hasOrdersWithStartkit = await this.orderOnBehalfService.hasStartkitOnOrders(
				this.modelGroup?.ProductLineCode
			);

			return (
				!accessToProductLine &&
				!hasOrdersWithStartkit &&
				this.brandModeService.isTitanium
			);
		}

		return false;
	}

	private executeAddToBagWithHandling(newOrderSelected?: boolean): void {
		this.bagExecutionService.executeWithBagHandling(async () => {
			try {
				await this.executeAddToBag(newOrderSelected);
			}
			catch (error) {
				console.log('error during executeAddToBag', error);
				throw error;
			}

			this.orderId = null;
		},
			async () => {
				await this.orderOnBehalfService.updateOrderRelevantData();
			},
			(error) => {
				this.currentCustomizationState = CustomizerStateConstants.NEWORDER; //Reenabling add to bag button
				throw error;
			}
		);
	}

	private executeStartkitAddToBagWithHandling(
		orderItem: OrderItemModel,
		customerNo: string | undefined,
		startKitModel: any,
		newOrderSelected?: boolean
	): void {
		if (!customerNo) return;

		this.bagExecutionService.executeWithBagHandling(
			async () => {
				const newStartkitMapping: StartkitMapping = {
					CustomerNo: customerNo,
					ProductLineCode: startKitModel.ProductLineCode,
					StartkitItemNo: startKitModel.ItemNo,
				};
				await this.startkitMappingService.postStartkitMapping(
					newStartkitMapping
				);
				await this.addItemToOrder(orderItem, newOrderSelected);
			},
			async () => {
				// Update customer data after successful completion
				await this.orderOnBehalfService.updateOrderRelevantData();
			}
		);
	}

	async executeAddToBag(newOrderSelected?: boolean): Promise<void> {
		const orderItem = OrderItemModel.MapFromVariants(
			null,
			this.orderId,
			this.isDemo,
			1,
			this.currentComment,
			this.currentReference,
			this.currentEngraving,
			this.baseConfiguration,
			this.modelGroup,
			this.model,
			this.chosenModelSize,
			this.chosenVariants.SelectedVariants,
			this.hasFixtures,
			this.hasForms,
			this.clipOnOnly,
			this.discount?.key,
			this.discount?.percentage,
			this.chosenTempleInclination,
			this.brandModeService.isPrecious,
			this.age,
			this.gender,
			this.firstPair,
			this.chosenCase
		);

		orderItem.IsLimited = this.isLimited;

		// The starter kit modal check is now handled before this method is called
		await this.addItemToOrder(orderItem, newOrderSelected);
	}

	async addItemToOrder(orderItem: OrderItemModel, newOrderSelected?: boolean) {
		try {
			if (this.parts && this.parts.length > 0) {
				const promises = this.parts.map((p) => {
					orderItem.Parts = p;
					return this.addFramesToOrder(orderItem, newOrderSelected);
				});

				await Promise.all(promises);
			}
			else {
				await this.addFramesToOrder(orderItem, newOrderSelected);
			}
		}
		catch (error) {
			console.error('Error during order processing:', error);
			return Promise.reject(new AddOrderItemExceptionModel('Error creating order item:', this.orderItemService.GetVariantString(orderItem)));
		}
	}

	async addFramesToOrder(
		orderItem: OrderItemModel,
		newOrderSelected?: boolean
	) {
		try {
			await this.orderService.AddFrameToOrder(orderItem, newOrderSelected);
			await this.updateDiscount(null, false);

			const user = await this.userService.GetUser();
			await this.googleAnalyticsService.eventEmitter(
				'add_to_cart',
				'ecommerce',
				`${user?.Uuid} added ${this.model?.Code} to bag`,
				50
			);

			const upsellingComponent = this.components
				.concat(this.components.selectMany((y) => y.subComponents))
				.find((y) => y.useForUpselling);

			if (upsellingComponent) {
				setTimeout(() => {
					if (upsellingComponent.parentMenu) {
						this.updateCurrentMainComponent(
							this.components.find(
								(y) => y.id == upsellingComponent.parentMenu.id
							)
						);
						this.updateCurrentComponent(upsellingComponent);
					}
					else {
						this.updateCurrentMainComponent(upsellingComponent);
						this.updateCurrentComponent(undefined);
					}
				}, 1000);
			}
		}
		catch (e) {
			if (e instanceof OrderCanceledError) {
				// do nothing apparently...
			}
			// else if (e instanceof OrderItemAskForOrderIdError) {
			// 	this.modalService.create(OrderChooserModalComponent, { orderSelect: this.orderSelectEmitter });
			// }
			else {
				throw e;
			}
		}
	}

	public GetChoosableVariants = (
		componentUuid?: string,
		variantUuid?: string,
		variantSizeUuid?: string
	): Promise<Array<ComponentVariantFromApiModel> | undefined> =>
		this.configurationService.getVariants(
			this.baseConfiguration?.ModelConfigurationUuid,
			this.modelGroup,
			componentUuid,
			variantUuid,
			variantSizeUuid
		);

	public mousedownOnSlider(event: any) {
		if (!event.touches) {
			this.mouseSumCoordinate = event.clientX + event.clientY;
		}
		else {
			this.mouseSumCoordinate =
				event.touches[0].clientX + event.touches[0].clientY;
		}
	}

	public closeCurrentComponent(event: any) {
		const target: HTMLElement = event.target as HTMLElement;
		if (event.target && target.classList.contains('thumb-button')) {
			return;
		}

		let mouseCoordinate = -1;

		if (!event.changedTouches) {
			mouseCoordinate = event.clientX + event.clientY;
		}
		else {
			mouseCoordinate = event.changedTouches[0].clientX + event.changedTouches[0].clientY;
		}

		if (mouseCoordinate - this.mouseSumCoordinate < 5 && mouseCoordinate - this.mouseSumCoordinate > -5) {
			setTimeout(() => {
				this.updateCurrentComponent(undefined);
				this.updateCurrentMainComponent(undefined);
			}, 200);
		}
	}

	public updateCollapsedOnMainComponent(collapsed: boolean) {
		if (this.currentMainComponent?.collapsed)
			this.currentMainComponent.collapsed = collapsed;

		if (this.currentMainComponent?.collapsed) {
			this.updateCurrentComponent(undefined);
		}

		if (this.currentMainComponent?.subComponents && this.currentMainComponent.subComponents.length > 0) {
			this.currentMainComponent.subComponents.forEach((subComponent) => {
				subComponent.isVisited = false;
			});
		}
	}

	public async updateDiscount(
		discountModel: DiscountModel | null,
		save: boolean = true
	) {
		this.discount = discountModel;

		if (save) {
			await this.saveChanges(true);
		}

		this.events.showLoader(false);
	}

	// todo: find a way to do this automatically
	public checkForDiscountErrors() {
		this.discountErrors = this.discount
			? this.discount.validate(
				new VariantsToDiscountModel(
					this.engravingToUpdate ?? this.currentEngraving,
					this.referenceToUpdate ?? this.currentReference,
					this.getVariantsWithChanges(
						this.chosenVariants?.Variants,
						this.variantsToUpdate
					)
				)
			)
			: new ValidationResult();

		this.components.forEach((x) => {
			if (MenuConstants.REFERENCE.some((m) => m === x.name)) {
				x.error = !this.discountErrors.isValid;
			}
		});
	}

	public async clientInfoIsMandatory() {
		// customer info is required if 1) user is a Partner ("Customer"), 2) app state is "Client" (not "Display"), 3) glasses model is "rimless", and 4) engraving text has been entered - otherwise not
		const user = await this.userService.GetUser();

		return (
			user?.WebResources &&
			user.hasMandatoryClientInfo &&
			user.Type === UserTypeConstant.Customer &&
			this.enduserDisplayService.IsClient() &&
			this.model?.IsRimless &&
			this.currentEngraving !== '' &&
			(!this.age || !this.gender || !this.firstPair)
		);
	}

	private switchMenuToMiscellaneous() {
		const referenceComponent = this.components.find((c) => c.translateName === 'MISCELLANEOUS');

		if (referenceComponent) {
			this.updateCurrentMainComponent(referenceComponent);
		}
	}

	public updateCurrentComponent(component: CustomizerMenuPartModel | undefined) {
		if (this.currentSubComponent) {
			this.menuService.setSubMenuIsExpanded(false);
			this.currentSubComponent.isVisited = true;
		}

		this.currentSubComponent = component;

		this.currentActualComponent = this.currentSubComponent;
	}

	public updateCurrentMainComponent(component: CustomizerMenuPartModel | undefined) {
		if (this.currentMainComponent) {
			this.menuService.setSubMenuIsExpanded(false);
			this.currentMainComponent.isVisited = true;
		}

		this.currentMainComponent = component;
	}

	public setCurrentImageByName(name: AngleConstants) {
		setTimeout(() => (this.currentImageName = name), 0);
	}

	public async updateReference(reference: string) {
		this.referenceToUpdate = reference;
		await this.saveChanges(true);
		this.events.showLoader(false);
	}

	public async updateComment(comment: string) {
		this.commentToUpdate = comment;
		await this.saveChanges(true);
		this.events.showLoader(false);
	}

	public async updateEngraving(engraving: string, save: boolean = true) {
		this.engravingToUpdate = engraving.trim();

		if (save) {
			await this.saveChanges(true);
		}

		this.events.showLoader(false);
	}

	public async updateModel(model: ModelModel | undefined, save: boolean = true) {
		if (this.modelToUpdate == null) {
			this.modelToUpdate = model;
			await this.getAndUpdateModelSize(model, save);
		}
	}

	public async getAndUpdateModelSize(model: ModelModel | undefined, save: boolean = true) {
		this.titleService.setTitle(model?.Code);

		const res = await this.dataApiService.getSizes(
			model?.ProductLineUuid,
			model?.ModelGroupUuid,
			model?.Uuid
		);

		let size: ModelSizeModel | undefined;

		if (this.isDemo && model?.IsRimless && res?.some((x) => x.Default)) {
			size = res.find((x) => x.Default);
		}
		else if (res?.some((x) => x.Code == this.chosenModelSize?.Code)) {
			size = res.find((x) => x.Code == this.chosenModelSize?.Code);
		}
		else if (res?.some((x) => x.Default)) {
			size = res.find((x) => x.Default);
		}
		else {
			size = res?.[0];
		}

		await this.updateModelSize(size, save);
	}

	public async updateModelSize(size?: ModelSizeModel, save: boolean = true) {
		this.modelsizeToUpdate = size;

		// console.debug("ModelSize changed, Getting new configuration");
		const configurations = await this.configurationService.getConfigurations(this.modelsizeToUpdate?.Uuid);

		let config: ConfigurationModel | undefined;

		if (!configurations.some((x) => x.ModelConfigurationUuid == this.baseConfiguration?.ModelConfigurationUuid)) {
			if (configurations.some((x) => x.Default)) {
				config = configurations.find((x) => x.Default);
			}
			else {
				config = configurations[0];
			}

			const configPlusModel = new ConfigurationPlusModel(config);
			await this.updateConfiguration(configPlusModel, true, save);
		}
		else {
			await this.setNewCaseIfNecessary(
				undefined,
				this.model?.Id,
				size,
				this.baseConfiguration?.ModelConfigurationUuid,
				this.modelGroup
			);

			if (save) {
				await this.saveChanges(true);
			}

			this.events.showLoader(false);
		}
	}

	public async updateConfiguration(
		configuration: ConfigurationPlusModel,
		updateBaseConfig = false,
		save: boolean = true
	) {
		const updateModel =
			this.chosenVariants.updateConfiguration(configuration);

		const variantTempleUpdateModel = updateModel.VariantChanges.map((x) => x.NewComponentVariant).find((x) => x?.Component.Code == ComponentCodeConstants.Temple);

		await this.setNewCaseIfNecessary(
			variantTempleUpdateModel,
			this.modelToUpdate?.Id ?? this.model?.Id,
			this.modelsizeToUpdate ?? this.chosenModelSize,
			updateModel.ConfigurationChange.ModelConfigurationUuid,
			this.modelGroup
		);

		if (
			this.modelGroup?.IsConfigurationBased ||
			this.modelGroup?.IsBothConfigAndVariantBased ||
			updateBaseConfig
		) {
			this.configurationToUpdate = updateModel.ConfigurationChange;
		}

		for (const variantChange of updateModel.VariantChanges) {
			this.pushToVariantsToUpdate(variantChange);

			if (variantChange.MenuTypesWithWarning && variantChange.MenuTypesWithWarning.length > 0) {
				if (!variantChange.OldComponentVariant?.NotSelected) {
					this.pushChangeWarning(variantChange);
				}
			}
		}

		await this.CheckForDependencyChanges(updateModel.VariantChanges);

		if (save) {
			await this.saveChanges(true);
		}

		this.events.showLoader(false);
	}

	public async updateVariantDesign(variant: ComponentVariantChangeModel) {
		if (variant.NewComponentVariant?.Component?.Code === ComponentCodeConstants.Temple) {
			this.isLimited = this.isLimitedTempleVariant(variant.NewComponentVariant);
		}

		const updateModel = await this.chosenVariants.updateVariantDesign(variant);

		if (updateModel.NewComponentVariant?.Component.Code == ComponentCodeConstants.Temple) {
			await this.setNewCaseIfNecessary(
				updateModel.NewComponentVariant,
				this.model?.Id,
				this.chosenModelSize,
				this.baseConfiguration?.ModelConfigurationUuid,
				this.modelGroup
			);
		}

		this.pushToVariantsToUpdate(updateModel);

		if (updateModel.MenuTypesWithWarning && updateModel.MenuTypesWithWarning.length > 0) {
			if (!updateModel.OldComponentVariant?.NotSelected) {
				this.pushChangeWarning(updateModel);
			}
		}

		this.pushDiscountUpdateWarning(updateModel);

		if (variant && variant.NewComponentVariant) {
			this.CheckDependencies(variant.NewComponentVariant);
		}

		await this.CheckForDependencyChanges([variant]);
		await this.saveChanges(true);

		await this.setNosepadToGoldIfPrecious(this.isLimited);

		this.events.showLoader(false);
	}

	public async updateComponentSize(variant: ComponentVariantChangeModel) {
		const updateModel = await this.chosenVariants.updateComponentSize(variant);

		this.pushToVariantsToUpdate(updateModel);

		if (updateModel.MenuTypesWithWarning && updateModel.MenuTypesWithWarning.length > 0) {
			if (!updateModel.OldComponentVariant?.NotSelected) {
				this.pushChangeWarning(updateModel);
			}
		}

		if (updateModel.NewComponentVariant?.Component.Code === ComponentCodeConstants.Temple) {
			await this.setNewCaseIfNecessary(
				updateModel.NewComponentVariant,
				this.model?.Id,
				this.chosenModelSize,
				this.baseConfiguration?.ModelConfigurationUuid,
				this.modelGroup
			);
		}

		await this.CheckForDependencyChanges([variant]);
		await this.saveChanges(true);

		this.events.showLoader(false);
	}

	public async updateVariant(variant: ComponentVariantChangeModel, resetToBaseConfig: boolean = false) {
		const variantsToUpdate: Array<ComponentVariantChangeModel> = [variant];
		const updateModels = variantsToUpdate;

		updateModels.forEach((updateModel) => {
			this.pushToVariantsToUpdate(updateModel);

			if (updateModel.MenuTypesWithWarning && updateModel.MenuTypesWithWarning.length > 0) {
				if (!updateModel.OldComponentVariant?.NotSelected) {
					this.pushChangeWarning(updateModel);
				}
			}
		});

		if (variant && variant.NewComponentVariant) {
			this.CheckDependencies(variant.NewComponentVariant);
		}

		await this.CheckForDependencyChanges([variant]);
		await this.saveChanges(true);

		this.events.showLoader(false);
	}

	originalMetal: any = null;
	matchMetal: any = null;

	async CheckDependencies(variant: ComponentVariantModel, count: number = 0) {
		const allVariantsWithChoices =
			await this.configurationService.getVariantsWithDisplayFilter(
				this.baseConfiguration?.ModelConfigurationUuid,
				this.modelGroup,
				null,
				null,
				null,
				this.enduserDisplayService.IsDisplay()
			);

		const otherVariants = this.chosenVariants.Variants;
		const componentId = variant.Component.Id;

		const allDependencies = this.allConfigurations[0].Dependencies;

		// FIND ALL DEPENDENCIES THAT INCLUDE/INFLUENCE THE NEWLY SELECTED COMPONENT (VARIANT)
		const dependencies = allDependencies.filter(
			(d) =>
				d.Component1.Id === componentId ||
				d.Component2.Id === componentId
		);

		if (dependencies) {
			for (let d of dependencies) {
				let defender = otherVariants
					?.filter((v) => v.Id !== variant.Id)
					.find(
						(v) =>
							v.Component.Id === d.Component1.Id ||
							v.Component.Id === d.Component2.Id
					);

				// COLOR JUNCTION
				if (d.ColorJunction > 0) {
					// COLOR GROUP
					if (d.ColorJunction === 1) {
						if (
							variant.VariantColor?.Color?.ColorGroup?.Id !=
							defender?.VariantColor?.Color?.ColorGroup?.Id
						) {
							// if the colorGroups are not equal, we try to find the exact variant color for the defender
							let newColor = allVariantsWithChoices
								?.find((x) => x.Id == defender?.Id)
								?.VariantSizes.find(
									(x) => x.Id == defender?.VariantSize?.Id
								)
								?.VariantColors.find(
									(x) =>
										x.Color?.Code ==
										variant.VariantColor?.Color?.Code
								);

							if (!newColor) {
								// if exact color is not found we get the first color in the same color group as the variant
								const colorGroupId =
									variant.VariantColor?.Color?.ColorGroup?.Id;

								let newSizes = allVariantsWithChoices?.find(
									(x) => x.Id == defender?.Id
								)?.VariantSizes;

								if (newSizes) {
									let newColors = newSizes.find(
										(x) => x.Id == defender?.VariantSize?.Id
									)?.VariantColors;

									if (newColors) {
										let newColorFromGroup = newColors.find(
											(x) =>
												x.Color?.ColorGroup?.Id ===
												colorGroupId
										);

										if (newColorFromGroup) {
											newColor = newColorFromGroup;
										}
									}
								}
							}

							if (newColor) {
								const newDefender = defender?.CreateCopy();
								if (newDefender) {
									newDefender.VariantColor = newColor;

									const result =
										new ComponentVariantChangeModel(
											defender,
											newDefender,
											null,
											undefined
										);

									result.MenuTypesWithWarning.push(
										MenuConstants.COLOR
									);

									this.pushChangeWarning(result);
								}
								this.originalMetal = defender?.VariantColor;
								this.matchMetal = variant.VariantColor;
							}
						}
					}
					// EXACT COLOR
					else {
						if (
							variant.VariantColor?.Color?.Id !=
							defender?.VariantColor?.Color?.Id
						) {
							// if the colorGroups are not equal, we try to find the exact variant color for the defender
							let newColor = allVariantsWithChoices
								?.find((x) => x.Id == defender?.Id)
								?.VariantSizes.find(
									(x) => x.Id == defender?.VariantSize?.Id
								)
								?.VariantColors.find(
									(x) =>
										x.Color?.Code ==
										variant.VariantColor?.Color?.Code
								);

							if (!newColor) {
								// if exact color is not found we get the first color in the same color group as the variant
								const colorGroupId =
									variant.VariantColor?.Color?.ColorGroup?.Id;
								newColor = allVariantsWithChoices
									?.find((x) => x.Id == defender?.Id)
									?.VariantSizes.find(
										(x) => x.Id == defender?.VariantSize?.Id
									)
									?.VariantColors.find(
										(x) =>
											x.Color?.ColorGroup?.Id ===
											colorGroupId
									);
							}

							const newDefender = defender?.CreateCopy();
							if (newDefender) {
								newDefender.VariantColor = newColor;
								const result = new ComponentVariantChangeModel(
									defender,
									newDefender,
									null,
									undefined
								);
								result.MenuTypesWithWarning.push(
									MenuConstants.COLOR
								);
								this.pushChangeWarning(result);
							}

							// this.matchMetal = variant.NewComponentVariant.VariantColor;
							this.matchMetal = variant.VariantColor;
						}
					}
				}

				// BINDING
				if (d.Binding > 0) {
					// BLOCK
					if (d.Binding === 1) {
						// variant wants to block out... new wide => gemstone
						// let c1 = otherVariants.find(v => (v.Component.Id === d.Component1.Id));
						// console.log(variant.Code);
						// console.log(c1.Code);
						// const b1 = this.allConfigurations[0].Bindings.filter(b => b.ComponentId === d.Component1.Id && b.ConfigurationId === d.ConfigurationId);
						// console.log('B1');
						// b1.forEach(x => console.log(x.ComponentVariant.Code));
						// const b2 = this.allConfigurations[0].Bindings.filter(b => b.ComponentId === d.Component2.Id);
						// console.log('B2');
						// b2.forEach(x => console.log(x.ComponentVariant?.Code));
					}
					// PERMIT ONLY
					else {
						// c2 can only appear in this form
						let c2 = otherVariants?.find(
							(v) => v.Component.Id === d.Component2.Id
						);

						console.log(c2);
					}
				}

				// this.CheckDependencies(defender, ++count);
			}
		}
	}

	public async updateClipOnOnly(clipOnOnly: boolean) {
		this.clipOnOnly = clipOnOnly;
		this.chosenVariants.ToggleClipOnOnly(clipOnOnly);
		await this.setNewCaseIfNecessary(
			this.chosenVariants.Variants?.[0],
			this.model?.Id,
			this.chosenModelSize,
			this.baseConfiguration?.ModelConfigurationUuid,
			this.modelGroup
		);

		await this.saveChanges(true);
		this.events.showLoader(false);
	}

	public async updateHasForms(hasForms: boolean) {
		this.hasForms = hasForms;
		await this.saveChanges(true);
	}

	public async updateHasFixtures(hasFixtures: boolean) {
		this.hasFixtures = hasFixtures;
		await this.saveChanges(true);
	}

	public async updateFirstPair(
		firstPair: FirstPair | null,
		save: boolean = true
	) {
		this.firstPair = firstPair;
		if (save) {
			await this.saveChanges(true);
		}
	}

	public async updateAge(age: string | null, save: boolean = true) {
		this.age = age;
		if (save) {
			await this.saveChanges(true);
		}
	}

	public async updateGender(gender: Gender | null, save: boolean = true) {
		this.gender = gender;
		if (save) {
			await this.saveChanges(true);
		}
	}

	public async highlightChangesOverlay() {
		if (this.errors.length > 0 && this.currentSubComponent?.isVisible) {
			this.showHighlightChangesOverlay = true;
			setTimeout(() => {
				this.showHighlightChangesOverlay = false;
			}, 100);
		}
	}

	public async updateTempleInclination(
		templeInclination: TempleInclinationEnum,
		save: boolean = true
	) {
		this.templeInclinationToUpdate = templeInclination;
		if (save) {
			await this.saveChanges(true);
		}
		this.events.showLoader(false);
	}

	public async updateCase(caseToUpdate: MiscModel, save: boolean = true) {
		this.caseToUpdate = caseToUpdate;
		if (save) {
			await this.saveChanges(true);
		}
		this.events.showLoader(false);
	}

	public async updateParts(parts: string[]) {
		this.parts = parts;

		await this.saveChanges(true);

		this.currentCustomizationState = CustomizerStateConstants.NEWORDER;
	}

	public async updateMenuVisited(menuModel: MenuModel) {
		// updateMenuVisited sets isVisited to true on menu and submenus
		// this is done so that RunDisablingSystem() is run for all relevant submenus
		const menuComponent = this.components.filter((c) =>
			c.subComponents?.some((s) => s?.name === menuModel?.Name)
		)[0];

		if (menuComponent) {
			menuComponent.isVisited = true;

			menuComponent.subComponents.forEach((subComponent) => {
				subComponent.isVisited = true;
			});
		}
	}

	public async matchColors(fromVariant: VariantColorModel) {
		console.debug('Updating all possible colors from', fromVariant);

		// get all model component variants; temple, upper rim, lower rim etc
		const selectableVariantsData = await this.getMatchableVariants();
		const variantsToEdit = this.getVariantsToEdit(selectableVariantsData);
		const changeModels = fromVariant.MatchColors(
			variantsToEdit,
			selectableVariantsData
		);
		await this.updateVariants(changeModels);
	}

	private getVariantsToEdit(
		variants?: Array<ComponentVariantFromApiModel>
	): Array<ComponentVariantModel> {
		const result: Array<ComponentVariantModel> = [];

		this.chosenVariants.Variants?.forEach((chosenVariant) => {
			if (variants?.some((x) => x.Uuid == chosenVariant.Uuid)) {
				result.push(chosenVariant);
			}
		});

		return result;
	}

	private async getMatchableVariants(): Promise<
		Array<ComponentVariantFromApiModel> | undefined
	> {
		const variants = this.chosenVariants.Variants;

		const subcomponents = this.components
			.map((x) => x.subComponents)
			.reduce((a, b) => a.concat(b));
		const totalComponents = this.components.concat(subcomponents);
		const components = totalComponents
			.map((x) => x.menuComponents)
			.reduce((a, b) => a.concat(b));

		const variantIds: Array<string> = [];

		components.forEach((x) => {
			const variantUuids = variants
				?.filter((s) =>
					x.ComponentVariantUuids != null
						? s.Uuid == x.ComponentVariantUuids
						: s.Component.Uuid == x.ComponentUuids
				)
				.map((y) => y.Uuid);
			variantUuids?.forEach((y) => {
				variantIds.push(y);
			});
		});

		const variantsFiltered = variants?.filter((v) => {
			for (let i = 0; i < variantIds.length; i++) {
				if (variantIds[i] == v.Uuid) {
					return true;
				}
			}

			return false;
		});

		const variantsFromApi: Array<
			Promise<Array<ComponentVariantFromApiModel> | undefined>
		> = [];

		variantsFiltered?.forEach((variant) => {
			variant.VariantColorViewed = true;
			const v = this.configurationService.getVariants(
				this.baseConfiguration?.ModelConfigurationUuid,
				this.modelGroup,
				variant.Component.Uuid,
				variant.Uuid,
				variant.VariantSize?.Uuid
			);
			variantsFromApi.push(v);
		});

		const variantList = await Promise.all(variantsFromApi);
		const result = variantList.map((x) => x?.[0]).filter((x) => x);

		return result.filter((x) => x !== undefined);
	}

	public async updateVariants(variants: Array<ComponentVariantChangeModel>) {
		let updateModels = new Array<ComponentVariantChangeModel>();

		variants.forEach((variant) => {
			updateModels = updateModels.concat(
				this.chosenVariants.updateVariant(variant)
			);
		});

		updateModels.forEach((updateModel) => {
			this.pushToVariantsToUpdate(updateModel);
			if (
				updateModel.MenuTypesWithWarning &&
				updateModel.MenuTypesWithWarning.length > 0
			) {
				if (!updateModel.OldComponentVariant?.NotSelected) {
					this.pushChangeWarning(updateModel);
				}
			}
		});

		await this.CheckForDependencyChanges(variants);
		await this.saveChanges(true);
		this.events.showLoader(false);
	}

	public async CheckForDependencyChanges(variantChangeModel: Array<ComponentVariantChangeModel>): Promise<void> {
		const variantsWithChanges = ChosenVariantsModel.getVariantsWithChanges(this.chosenVariants?.Variants, variantChangeModel);

		const updateModels = await this.dependencyCalculator.GetDependencyChanges(variantsWithChanges, variantsWithChanges, this.baseConfiguration);

		updateModels.forEach((updateModel) => {
			this.pushToVariantsToUpdate(updateModel);

			if (updateModel?.MenuTypesWithWarning && updateModel.MenuTypesWithWarning.length > 0) {
				if (!updateModel.OldComponentVariant?.NotSelected) {
					this.pushChangeWarning(updateModel);
				}
			}
		});
	}

	public async saveChangesTempleLengthCheck(save: boolean) {
		this.resetErrors(save);

		await this.saveChanges(save);

		const temple = this.components.find((x) => x.name === 'Temple');
		if (temple) {
			const templeLength = temple.subComponents?.find(
				(y) => y.name === 'TempleLength'
			);
			if (templeLength) {
				templeLength.isVisited = false;
			}
		}
	}

	private resetErrors(ok: boolean) {
		if (ok) {
			this.errors.forEach((errorModel) => {
				const parentMenu = this.components.find(
					(x) => x.id == errorModel.menuId
				);
				const subMenu = this.components
					.map((x) => x.subComponents)
					.reduce((x, y) => x.concat(y))
					.find((x) => x.id == errorModel.menuId);

				if (parentMenu) {
					parentMenu.isVisited = false;
				}

				if (subMenu) {
					subMenu.isVisited = false;

					if (subMenu.parentMenu) {
						subMenu.parentMenu.isVisited = false;
					}
				}
			});

			this.events.errorsWereReset.emit(true);
		}

		this.errors = [];
	}

	public async saveChanges(save: boolean) {
		// console.debug("save changes initiated", this.errors);
		this.checkForDiscountErrors();

		if (
			this.errors.length < 1 &&
			(this.discountErrors.isValid || this.discountToReset)
		) {
			if (save) {
				if (
					this.currentCustomizationState ==
					CustomizerStateConstants.EDITORDER
				) {
					this.orderChangedEmitter.next(0);
				} else {
					this.currentCustomizationState =
						CustomizerStateConstants.NEWORDER;
					await this.setCurrentModels();
					this.cleanUpdateValues();
				}

				// if (this.parts) {
				// 	this.partsCollection = this.parts
				// }
			} else {
				this.cleanUpdateValues();
			}
		} else {
			this.events.showLoader(false);
		}

		if (save && this.matchMetal) {
			console.log(
				'color or color group was changed and user clicked ok to change'
			);

			// TODO: This updates colors on all applicable variants. We need a function to just update the color on one.
			this.matchColors(this.matchMetal);

			this.matchMetal = null;
		} else if (!save && this.originalMetal) {
			// user has clicked cancel after being presented with the "change color group warning dialog"
			// so here we should roll the color back to the original

			// ???

			this.originalMetal = null;
		}
	}

	private async updateOrderOnChanged() {
		await this.setCurrentModels();
		this.SetOrderlinesOnVariants(
			this.chosenVariants.SelectedVariants,
			this.orderItem?.OrderLines
		);

		const updatedOrderItem = OrderItemModel.MapFromVariants(
			this.orderItem?.Uuid,
			this.orderItem?.OrderId,
			this.isDemo,
			this.orderItem?.Amount,
			this.currentComment,
			this.currentReference,
			this.currentEngraving,
			this.baseConfiguration,
			this.modelGroup,
			this.model,
			this.chosenModelSize,
			this.chosenVariants.SelectedVariants,
			this.hasFixtures,
			this.hasForms,
			this.clipOnOnly,
			this.discount?.key,
			this.discount?.percentage,
			this.chosenTempleInclination,
			this.brandModeService.isPrecious,
			this.age,
			this.gender,
			this.firstPair,
			this.chosenCase
		);

		updatedOrderItem.CustomerNo = this.orderItem?.CustomerNo;
		await this.updateOrder(updatedOrderItem);
		this.cleanUpdateValues();
	}

	private async updateOrder(
		orderItemEditModel: OrderItemModel
	): Promise<void> {
		try {
			const orderItem = await this.orderDataService.editOrderItem(orderItemEditModel);

			// Update order uuids default variant models change (e.g. adds or removes a groove color)
			this.orderItem = orderItem;

			this.chosenVariants.Variants?.filter(
				(x) => x && !x.OrderLineUuid
			).forEach((x) => {
				const orderLine = this.orderItem?.OrderLines.find(
					(t) => t.ComponentVariantUuid == x.Uuid
				);
				if (orderLine) {
					x.OrderLineUuid = orderLine.Uuid;
				}
			});

			this.chosenVariants.Variants?.filter(
				(x) => x.NotSelected
			).forEach((x) => {
				const orderLine = this.orderItem?.OrderLines.find(
					(t) => t.ComponentVariantUuid == x.Uuid
				);
				if (!orderLine) {
					x.OrderLineUuid = undefined;
				}
			});
		} catch (httpError: any) {
			const errorMessage: string =
				httpError.error?.Message || httpError.error;

			if (
				errorMessage ==
				ErrorResponseConstants.OrderConcurrencyChanged
			) {
				this.modalService.create(ApiResponseModalComponent, {
					responseMessage: this.translateService.instant(
						`BAG.BAGITEMCHANGEDCONFLICT`
					),
				});
			} else if (
				errorMessage ==
				ErrorResponseConstants.OrderItemNotExistInBag
			) {
				this.modalService.create(ApiResponseModalComponent, {
					responseMessage:
						this.translateService.instant(
							`BAG.BAGITEMNOTEXIT`
						),
				});
			} else {
				throw httpError;
			}
		}
	}

	private SetOrderlinesOnVariants(
		variants: Array<ComponentVariantModel> | undefined,
		orders: Array<OrderLine> | undefined
	): Array<ComponentVariantModel> | undefined {
		variants?.forEach((variant) => {
			const currentVariant = orders?.find(
				(x) => x.ComponentVariantUuid == variant.Uuid
			);

			if (currentVariant) {
				variant.OrderLineUuid = currentVariant.Uuid;
			}
		});

		return variants;
	}

	public async setCurrentModels() {
		if (this.modelToUpdate != null) {
			console.debug('Saving model', this.modelToUpdate);
			const newChosenVariants = await this.updateDefaultVariants();

			this.chosenVariants = new ChosenVariantsModel(
				newChosenVariants,
				this.sorter,
				this.GetChoosableVariants
			);

			if (this.model?.Code && this.modelToUpdate?.Code) {
				await this.navigateService.UpdateUrl(
					this.model.Code,
					this.modelToUpdate.Code
				);
			}

			this.model = this.modelToUpdate;

			// setting variants after dependency run
			if (
				this.variantsToUpdate != null &&
				this.variantsToUpdate.length > 0
			) {
				console.debug('Saving variants', this.variantsToUpdate);
				this.chosenVariants.SetVariants(
					ChosenVariantsModel.getVariantsWithChanges(
						this.chosenVariants.Variants,
						this.variantsToUpdate
					)
				);
				this.appState.priceWasPossiblyInfluenced.next(null);
				this.clipOnIncluded =
					this.chosenVariants.SelectedVariants?.some(
						(variant) =>
							variant.Component.Code ==
							ComponentCodeConstants.Clipon
					);
			}

			await this.InitCurrentMenus();
		} else if (
			this.variantsToUpdate != null &&
			this.variantsToUpdate.length > 0
		) {
			// console.debug("Saving variants", this.variantsToUpdate);
			this.chosenVariants.SetVariants(
				ChosenVariantsModel.getVariantsWithChanges(
					this.chosenVariants.Variants,
					this.variantsToUpdate
				)
			);
			this.appState.priceWasPossiblyInfluenced.next(null);
			this.clipOnIncluded = this.chosenVariants.SelectedVariants?.some(
				(variant) =>
					variant.Component.Code == ComponentCodeConstants.Clipon
			);
		}

		if (this.modelsizeToUpdate != null) {
			console.debug('Saving modelsize', this.modelsizeToUpdate);
			this.chosenModelSize = this.modelsizeToUpdate;
		}

		if (this.configurationToUpdate != null) {
			console.debug('Saving configuration', this.configurationToUpdate);
			this.baseConfiguration = this.configurationToUpdate;
		}

		if (this.referenceToUpdate != null) {
			console.debug('Saving reference', this.referenceToUpdate);
			this.currentReference = this.referenceToUpdate;
		}

		if (this.commentToUpdate != null) {
			console.debug('Saving comment', this.commentToUpdate);
			this.currentComment = this.commentToUpdate;
		}

		if (this.engravingToUpdate != null) {
			console.debug('Saving engraving', this.engravingToUpdate);

			const variant = this.chosenVariants.Variants?.find(
				(obj) => obj.Component.Code == ComponentCodeConstants.Temple
			);
			const changedVariant = Object.assign(
				new ComponentVariantModel(variant?.Discontinued, variant?.New),
				variant
			);
			changedVariant.Engraving = this.engravingToUpdate;
			const changeModel = new ComponentVariantChangeModel(
				variant,
				changedVariant,
				null
			);
			this.chosenVariants.SetVariants(
				ChosenVariantsModel.getVariantsWithChanges(
					this.chosenVariants.Variants,
					new Array<ComponentVariantChangeModel>(changeModel)
				)
			);

			this.engravingService.engravingRepeat = this.engravingToUpdate;
			this.engravingService.engraving = this.engravingToUpdate;
			this.currentEngraving = this.engravingToUpdate;
		}

		if (this.discountToReset) {
			await this.updateDiscount(null, false);
			this.checkForDiscountErrors();
		}

		if (this.templeInclinationToUpdate != null) {
			console.debug(
				'Saving temple inclination',
				this.templeInclinationToUpdate
			);
			this.chosenTempleInclination = this.templeInclinationToUpdate;
		}

		if (this.caseToUpdate != null) {
			console.debug('Saving case', this.caseToUpdate);
			this.chosenCase = this.caseToUpdate;
		}

		if (this.removeCase) {
			this.chosenCase = null;
			this.removeCase = false;
		}

		this.appState.priceWasPossiblyInfluenced.next(null);

		this.clipOnImageUrlLow = null;
		this.clipOnImageUrlHigh = null;

		setTimeout(() => {
			this.clipOnImageUrlLow = this.getClipOnImageUrl('Low');
			this.clipOnImageUrlHigh = this.getClipOnImageUrl('High');
		}, 100);
	}

	private async updateDefaultVariants(): Promise<
		Array<ComponentVariantModel>
	> {
		const newChosenVariants = new Array<ComponentVariantModel>();
		let possibleVariants = new Array<ComponentVariantFromApiModel>();

		const result = await this.configurationService.getVariants(
			this.configurationToUpdate
				? this.configurationToUpdate.ModelConfigurationUuid
				: this.baseConfiguration?.ModelConfigurationUuid,
			this.modelGroup,
			undefined,
			undefined,
			undefined
		);
		if (result) possibleVariants = result;
		this.modelToUpdate?.DefaultVariantModels.forEach((defaultVariant) => {
			const chosenVariant = this.chosenVariants.Variants?.find(
				(x) => x.Component.Code == defaultVariant.Component.Code
			);

			if (chosenVariant) {
				const possibleVariant = possibleVariants.find(
					(x) =>
						x.Code == chosenVariant.Code &&
						x.Component.Uuid == chosenVariant.Component.Uuid
				);

				if (
					chosenVariant.VariantViewed ||
					this.currentCustomizationState ==
					CustomizerStateConstants.EDITORDER
				) {
					if (possibleVariant) {
						defaultVariant = chosenVariant;
					}
				}

				if (
					(chosenVariant.VariantColorViewed ||
						this.currentCustomizationState ==
						CustomizerStateConstants.EDITORDER) &&
					chosenVariant.VariantColor
				) {
					const possibleColor =
						possibleVariant &&
						possibleVariant.VariantSizes.map((y) => y.VariantColors)
							.reduce((prev, next) => prev.concat(next))
							.some(
								(x) =>
									x.Color?.Code ==
									chosenVariant.VariantColor?.Color?.Code
							);

					if (possibleColor) {
						defaultVariant.VariantColor =
							chosenVariant.VariantColor;
						defaultVariant.NotSelected = false;
					} else {
						defaultVariant.VariantColor = undefined;
						defaultVariant.NotSelected = true;
					}

					defaultVariant.VariantColorViewed = true;
				}

				if (
					(chosenVariant.VariantSizeViewed ||
						this.currentCustomizationState ==
						CustomizerStateConstants.EDITORDER) &&
					chosenVariant.VariantSize
				) {
					const possibleSize =
						possibleVariant &&
						possibleVariant.VariantSizes.some(
							(x) => x.Size == chosenVariant.VariantSize?.Size
						);

					if (possibleSize) {
						defaultVariant.VariantSize = chosenVariant.VariantSize;
					} else {
						defaultVariant.VariantSize =
							possibleVariant?.VariantSizes.length ?? 0 > 0
								? possibleVariant?.VariantSizes[0]
								: null;
					}

					defaultVariant.VariantSizeViewed = true;
				}
			}

			newChosenVariants.push(defaultVariant);
		});

		await this.CheckForDependencyChanges([]);

		return newChosenVariants;
	}

	public applyFavoriteCollectionChange(isInCollection: boolean) {
		this.isInCollection = isInCollection;
	}

	private cleanUpdateValues() {
		this.modelToUpdate = undefined;
		this.modelsizeToUpdate = undefined;
		this.configurationToUpdate = null;
		this.referenceToUpdate = null;
		this.engravingToUpdate = null;
		this.commentToUpdate = null;
		this.variantsToUpdate = undefined;
		this.discountToReset = false;
		this.caseToUpdate = null;
		this.templeInclinationToUpdate = undefined;
		this.checkForDiscountErrors();
		this.buildSummaryModels();
	}

	private pushDiscountUpdateWarning(
		updateModel: ComponentVariantChangeModel
	) {
		if (
			updateModel.MenuTypesWithWarning.some(
				(x) => x == MenuConstants.ENGRAVING
			) &&
			!updateModel.NewComponentVariant?.EngravingPossible &&
			!updateModel.NewEngraving &&
			this.discount?.key == DiscountConstants.SF
		) {
			const efDiscountErrorModel = new ErrorModel();
			efDiscountErrorModel.translateName = this.translateService.instant(
				'CUSTOMIZER.CHANGES.FROMTO',
				{
					from: this.translateService.instant('DISCOUNT.TITLE', {
						discount: this.discount?.key,
						percentage: this.discount?.percentage,
					}),
					to: this.translateService.instant('COMMON.NONE'),
					component: this.translateService.instant(
						`CUSTOMIZER.COMPONENTS.REFERENCE.NAME`
					),
					type: '',
				}
			);

			this.discountToReset = true;
			this.errors.push(efDiscountErrorModel);
		}
	}

	private pushChangeWarning(variant: ComponentVariantChangeModel) {
		const templeLenghtVisited = variant?.OldComponentVariant?.VariantSizeViewed || variant?.NewComponentVariant?.VariantSizeViewed;

		variant.MenuTypesWithWarning.forEach((menutyp) => {
			const menuWithProblem = this.identifyMenuWithProblems(variant, menutyp);

			let errorModel: ErrorModel | null = null;

			if (menuWithProblem) {
				if (menuWithProblem.parentMenu == null) {
					errorModel = new ErrorModel();
					errorModel.menuId = menuWithProblem.id;

					switch (menutyp) {
						case MenuConstants.ENGRAVING:
							if (variant.NewEngraving.length) {
								errorModel.translateName2 =
									this.translateService.instant(
										'CUSTOMIZER.CHANGES.MAKEANOTHERCHOICE',
										{
											page: this.translateService.instant(
												'CUSTOMIZER.COMPONENTS.' +
												menuWithProblem.translateName +
												'.NAME'
											),
										}
									);
							}
							else {
								errorModel.translateName2 = '';
							}
							break;
						default:
							errorModel.translateName2 =
								this.translateService.instant(
									'CUSTOMIZER.CHANGES.MAKEANOTHERCHOICE',
									{
										page: this.translateService.instant(
											'CUSTOMIZER.COMPONENTS.' +
											menuWithProblem.translateName +
											'.NAME'
										),
									}
								);
					}
				}
				else {
					errorModel = new ErrorModel();
					errorModel.menuId = menuWithProblem.id;

					const page =
						this.translateService.instant(
							'CUSTOMIZER.COMPONENTS.' +
							menuWithProblem.parentMenu.translateName +
							'.NAME'
						) +
						'/' +
						this.translateService.instant(
							'CUSTOMIZER.COMPONENTS.' +
							menuWithProblem.translateName +
							'.NAME'
						);

					switch (menutyp) {
						case MenuConstants.GROOVECOLOR:
							errorModel.translateName2 =
								this.translateService.instant(
									'CUSTOMIZER.CHANGES.DISPLAYENDUSER'
								);
							break;

						case MenuConstants.SIZE:
							if (templeLenghtVisited) {
								break;
							} else {
								errorModel.translateName2 =
									this.translateService.instant(
										'CUSTOMIZER.CHANGES.MAKEANOTHERCHOICE',
										{ page }
									);
								break;
							}

						default:
							errorModel.translateName2 =
								this.translateService.instant(
									'CUSTOMIZER.CHANGES.MAKEANOTHERCHOICE',
									{ page }
								);
					}
				}

				switch (menutyp) {
					case MenuConstants.ENGRAVING:
						errorModel.translateName =
							this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant
										?.Engraving,
									to: variant.NewEngraving.length
										? variant.NewEngraving
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.ENGRAVING.NAME`
									),
									type: '',
								}
							);
						break;

					case MenuConstants.GROOVECOLOR:
					case MenuConstants.COLOR:
						errorModel.translateName =
							this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant
										?.VariantColor?.Color?.Description,
									to: variant.NewComponentVariant
										?.VariantColor
										? variant.NewComponentVariant
											.VariantColor.Color?.Description
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.${variant.NewComponentVariant?.Component.Code}.NAME`
									),
									type:
										' ' +
										this.translateService.instant(
											'CUSTOMIZER.CHANGES.COLOR'
										),
								}
							);
						break;

					case MenuConstants.SIZE:
						if (templeLenghtVisited) {
							const translateName = this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant
										?.VariantSize?.Description,
									to: variant.NewComponentVariant?.VariantSize
										? variant.NewComponentVariant
											.VariantSize.Description
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.${variant.NewComponentVariant?.Component.Code}.NAME`
									),
									type:
										' ' +
										this.translateService.instant(
											'CUSTOMIZER.CHANGES.SIZE'
										),
								}
							);
							this.errormodelTempleSizeTranslateName =
								translateName;
							errorModel.translateName = translateName;
						}
						break;

					case MenuConstants.DESIGN:
						errorModel.translateName =
							this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant
										?.Description,
									to: !variant.NewComponentVariant
										?.NotSelected
										? variant.NewComponentVariant
											?.Description
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.${variant.NewComponentVariant?.Component.Code}.NAME`
									),
									type:
										' ' +
										this.translateService.instant(
											'CUSTOMIZER.CHANGES.VARIANT'
										),
								}
							);
						break;

					case MenuConstants.GEMSTONE:
						errorModel.translateName =
							this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant
										?.VariantSize?.Description,
									to: !variant.NewComponentVariant
										?.NotSelected
										? variant.NewComponentVariant
											?.VariantSize?.Description
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.${variant.NewComponentVariant?.Component.Code}.NAME`
									),
									type:
										' ' +
										this.translateService.instant(
											'CUSTOMIZER.CHANGES.VARIANT'
										),
								}
							);
						break;

					case MenuConstants.FINISH:
						errorModel.translateName =
							this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant?.Code,
									to: variant.NewComponentVariant?.Code
										? variant.NewComponentVariant.Code
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.${variant.NewComponentVariant?.Component.Code}.NAME`
									),
									type:
										' ' +
										this.translateService.instant(
											'CUSTOMIZER.CHANGES.COLOR'
										),
								}
							);
						break;

					default:
						errorModel.translateName =
							this.translateService.instant(
								'CUSTOMIZER.CHANGES.FROMTO',
								{
									from: variant.OldComponentVariant
										?.VariantSize?.Description,
									to: variant.NewComponentVariant?.NotSelected
										? variant.NewComponentVariant
											.VariantSize?.Description
										: this.translateService.instant(
											'COMMON.NONE'
										),
									component: this.translateService.instant(
										`CUSTOMIZER.COMPONENTS.${variant.NewComponentVariant?.Component.Code}.NAME`
									),
									type: '',
								}
							);
						break;
				}
			}

			if (menutyp === MenuConstants.SIZE && !templeLenghtVisited) {
				return;
			}

			if (errorModel) {
				if (
					!this.errors.find(
						(x) =>
							x.menuId == errorModel.menuId &&
							x.translateName == errorModel.translateName &&
							x.translateName2 == errorModel.translateName2
					)
				) {
					this.errors.push(errorModel);
				}
			}
		});
	}

	private identifyMenuWithProblems(
		variant: ComponentVariantChangeModel,
		menuTypes: Array<string>
	): CustomizerMenuPartModel | null | undefined {
		const variantChanged = variant.NewComponentVariant
			? variant.NewComponentVariant
			: variant.OldComponentVariant;

		if (menuTypes == MenuConstants.ENGRAVING) {
			const engravingParentMenu = this.components.find(
				(component) =>
					menuTypes.some((menuType) => menuType == component.name) &&
					(component.isVisited ||
						this.currentCustomizationState ==
						CustomizerStateConstants.EDITORDER)
			);

			if (engravingParentMenu) {
				return engravingParentMenu;
			}
		} else {
			// search all menu components for elements that correspond to the variants component
			let parentMenu = this.components.find(
				(component) =>
					component.menuComponents.some(
						(menuComponent) =>
							menuComponent.ComponentUuids ===
							variantChanged?.Component.Uuid
					) &&
					menuTypes.some((menuType) => menuType === component.name) &&
					(component.isVisited ||
						this.currentCustomizationState ===
						CustomizerStateConstants.EDITORDER)
			);

			if (parentMenu) {
				return parentMenu;
			} else {
				parentMenu = this.components.find((component) =>
					component.subComponents.some((subComponent) => {
						const subCompMenuAny = subComponent.menuComponents.some(
							(menuComponent) =>
								menuComponent.ComponentUuids ===
								variantChanged?.Component.Uuid
						);
						const menTypAny = menuTypes.some(
							(menuType) => menuType === subComponent.name
						);

						return subCompMenuAny && menTypAny;
					})
				);

				if (parentMenu) {
					const subMenu = parentMenu.subComponents.find(
						(subComponent) =>
							subComponent.menuComponents.some(
								(menuComponent) =>
									menuComponent.ComponentUuids ==
									variantChanged?.Component.Uuid
							)
					);

					if (this.currentSubComponent) {
						return subMenu;
					}

					if (
						this.currentMainComponent &&
						this.currentMainComponent.subComponents.length === 0
					) {
						return subMenu;
					}
				}
			}
		}

		return null;
	}

	public getVariantsWithChanges = (
		chosenVariants: Array<ComponentVariantModel> | undefined,
		updates?: Array<ComponentVariantChangeModel | null>
	): Array<ComponentVariantModel> =>
		ChosenVariantsModel.getVariantsWithChanges(chosenVariants, updates);

	private pushToVariantsToUpdate(
		variantChangeModel: ComponentVariantChangeModel | null
	) {
		const newVariantsToChange =
			this.variantsToUpdate?.flatMap((x) => x) ?? [];
		if (variantChangeModel) {
			newVariantsToChange?.push(variantChangeModel);
			this.variantsToUpdate = newVariantsToChange;
		}

		if (variantChangeModel?.NewEngraving != null) {
			this.engravingToUpdate = variantChangeModel.NewEngraving;
		}
	}

	private async setNewCaseIfNecessary(
		variantModel?: ComponentVariantModel,
		modelId?: number,
		modelSize?: ModelSizeModel,
		modelconfigUuid?: string,
		modelgroup?: ModelGroupModel
	) {
		if (this.clipOnOnly || this.brandModeService.isPrecious) {
			this.removeCase = true;
			return;
		}
		let varModel: ComponentVariantModel | undefined = undefined;
		if (!variantModel) {
			varModel = this.chosenVariants.Variants?.find(
				(x) => x.Component.Code == ComponentCodeConstants.Temple
			);
		}

		if (!variantModel) {
			this.removeCase = true;
			return;
		}

		const cases = await this.configurationService.getCases(
			modelId,
			modelSize,
			modelconfigUuid,
			modelgroup,
			varModel?.Component.Uuid,
			varModel?.Uuid,
			varModel?.VariantSize?.Uuid
		);

		if (cases.length === 0) {
			this.removeCase = true;
		} else if (
			!this.chosenCase ||
			!cases.some(
				(cas) =>
					cas.MiscModel.EcommerceNoUnique ===
					this.chosenCase?.EcommerceNoUnique
			)
		) {
			this.caseToUpdate = cases
				.filter((t) => t.Default)
				?.first()?.MiscModel;
		}
	}

	private async setNosepadToGoldIfPrecious(isLimited: boolean) {
		if (!isLimited) {
			return;
		}
		let varModel: ComponentVariantModel | undefined =
			this.chosenVariants.Variants?.find(
				(x) => x.Component.Code == ComponentCodeConstants.Nosepad
			);

		if (varModel?.Code !== 'S') {
			return;
		}
		var variantFromApi = this.variantsFromApiSignal()
			?.filter((x) => x.Order === 'METAL')
			.first();
		var currentVariant = this.currentVariantsSignal()
			?.filter((x) => x.Order === 'METAL')
			.first();

		const updateModel = new ComponentVariantChangeModel(
			varModel,
			currentVariant,
			variantFromApi?.VariantSizes
		);

		this.pushToVariantsToUpdate(updateModel);

		if (
			updateModel.MenuTypesWithWarning &&
			updateModel.MenuTypesWithWarning.length > 0
		) {
			if (!updateModel.OldComponentVariant?.NotSelected) {
				this.pushChangeWarning(updateModel);
			}
		}
		this.pushDiscountUpdateWarning(updateModel);

		if (updateModel && updateModel.NewComponentVariant) {
			this.CheckDependencies(updateModel.NewComponentVariant);
		}

		await this.CheckForDependencyChanges([updateModel]);
		await this.saveChanges(true);
	}

	toggleConfiguration() {
		this.showSummary = !this.showSummary;

		this.subMenuStateTogglerService.toggle.emit(
			this.showSummary
				? SubMenuStateEnum.configOpen
				: SubMenuStateEnum.configClose
		);

		this.menuService.setSubMenuIsExpanded(false);
	}

	public showAddToInspirationButton() {
		return (
			this.isUserInAdminMode &&
			this.userCanEditinspiration &&
			this.modelGroup?.FullFrame
		);
	}

	hideSunDetails() {
		this.sunDetailsStore.setShowSunDetails(false);
	}

	private hasLimitedTempleVariantSelected(): boolean {
		return this.allVariants?.some(variant => this.isLimitedTempleVariant(variant)) ?? false;
	}

	private isLimitedTempleVariant(variant?: ComponentVariantModel | null): boolean {
		if (variant?.Component?.Code !== ComponentCodeConstants.Temple) {
			return false;
		}

		const groupDescription = variant.GroupDescription?.Fallback?.trim().toLowerCase();

		return groupDescription === 'limited' || groupDescription === '';
	}

}

@Injectable({
	providedIn: 'root',
})
export class ModelChangedService {
	modelChanged: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
}
